import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, Shield, Lock, ArrowLeft, RadioTower, KeyRound, CheckCircle } from 'lucide-react';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Enter your email'); return; }
    setLoading(true);
    try {
      const r = await authAPI.forgotPassword({ email });
      setUserId(r.data.user_id);
      toast.success(r.data.message);
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    setLoading(true);
    try {
      const r = await authAPI.verifyOtp({ user_id: userId, otp });
      setResetToken(r.data.reset_token);
      toast.success('OTP verified!');
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid OTP'); } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await authAPI.resetPassword({ reset_token: resetToken, new_password: newPassword });
      toast.success('Password reset successfully!');
      setStep(4);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-white relative" data-testid="forgot-password-page">
      <div className="absolute inset-0 dot-grid-bg pointer-events-none" />
      <div className="w-full max-w-[420px] relative z-10">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#4680ff] to-[#2563eb] shadow-lg shadow-blue-500/20">
            <RadioTower className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div className="leading-none">
            <span className="text-[16px] font-bold text-slate-900 tracking-wide block">TRIPATHY</span>
            <span className="text-[10px] font-bold text-orange-500 tracking-[0.2em]">TELECOM</span>
          </div>
        </div>

        {step === 4 ? (
          <div className="text-center animate-fade-in">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Password Reset!</h2>
            <p className="text-sm text-slate-500 mb-8">Your password has been reset successfully</p>
            <Button onClick={() => navigate('/login')} className="w-full h-12 rounded-xl text-white font-semibold bg-[#4680ff] hover:bg-[#2563eb] shadow-lg shadow-blue-500/20 btn-press" data-testid="go-login-btn">
              Sign In Now
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900">
                {step === 1 ? 'Reset Password' : step === 2 ? 'Enter OTP' : 'New Password'}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {step === 1 ? 'Enter your email to receive an OTP on WhatsApp' : step === 2 ? 'Enter the 6-digit OTP sent to your WhatsApp' : 'Choose a new password for your account'}
              </p>
            </div>

            {/* Progress */}
            <div className="flex items-center gap-2 mb-6">
              {[1, 2, 3].map(s => (
                <div key={s} className="flex-1">
                  <div className={`h-1 rounded-full transition-all duration-300 ${s <= step ? 'bg-[#4680ff]' : 'bg-slate-200'}`} />
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              {step === 1 && (
                <form onSubmit={handleSendOtp} className="space-y-5">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)}
                        className="pl-11 h-12 bg-slate-50/80 border-slate-200 rounded-xl text-sm" data-testid="fp-email" />
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl text-white font-semibold bg-[#4680ff] hover:bg-[#2563eb] shadow-lg shadow-blue-500/20 btn-press" data-testid="fp-send-otp">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send OTP'}
                  </Button>
                </form>
              )}

              {step === 2 && (
                <form onSubmit={handleVerifyOtp} className="space-y-5">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">6-Digit OTP</Label>
                    <div className="relative">
                      <Shield className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input type="text" placeholder="000000" maxLength={6} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                        className="pl-11 h-12 bg-slate-50/80 border-slate-200 rounded-xl text-center text-xl tracking-[0.5em] font-bold font-mono" data-testid="fp-otp" />
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl text-white font-semibold bg-[#4680ff] hover:bg-[#2563eb] shadow-lg shadow-blue-500/20 btn-press" data-testid="fp-verify-otp">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify OTP'}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full text-xs text-slate-500" onClick={() => { setStep(1); setOtp(''); }}>
                    Resend OTP
                  </Button>
                </form>
              )}

              {step === 3 && (
                <form onSubmit={handleResetPassword} className="space-y-5">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input type="password" placeholder="Min 6 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                        className="pl-11 h-12 bg-slate-50/80 border-slate-200 rounded-xl text-sm" data-testid="fp-new-pw" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Confirm Password</Label>
                    <div className="relative">
                      <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input type="password" placeholder="Re-enter password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                        className="pl-11 h-12 bg-slate-50/80 border-slate-200 rounded-xl text-sm" data-testid="fp-confirm-pw" />
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl text-white font-semibold bg-[#4680ff] hover:bg-[#2563eb] shadow-lg shadow-blue-500/20 btn-press" data-testid="fp-reset">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
                  </Button>
                </form>
              )}
            </div>
          </>
        )}

        <p className="text-center mt-8 text-sm text-slate-500">
          <Link to="/login" className="font-semibold text-[#4680ff] hover:text-[#4680ff] flex items-center justify-center gap-1 transition-colors" data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to Login
          </Link>
        </p>
      </div>
    </div>
  );
};
export default ForgotPassword;
